import { isUnviewableModelUrl } from './mediaTypeDetection';

export interface ModelStats {
  vertices: number;
  triangles: number;
  meshes: number;
  materials: number;
  textures: number;
  fileSize: number;
}

type ParsedModelStats = Omit<ModelStats, 'fileSize'>;

const GLB_MAGIC = 0x46546c67;
const JSON_CHUNK_TYPE = 0x4e4f534a;
const GLB_HEADER_LENGTH = 12;
const CHUNK_HEADER_LENGTH = 8;
const GLB_JSON_START = GLB_HEADER_LENGTH + CHUNK_HEADER_LENGTH;
// Geometry metadata is normally tiny. Capping JSON prevents an extensionless,
// chunked response from making the info panel retain an arbitrarily large file.
const MAX_MODEL_JSON_BYTES = 4 * 1024 * 1024;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalArray(value: unknown): unknown[] | null {
  return value === undefined ? [] : Array.isArray(value) ? value : null;
}

function accessorCount(accessors: Array<Record<string, unknown>>, index: unknown): number {
  if (!Number.isInteger(index) || (index as number) < 0) return 0;
  const accessor = accessors[index as number];
  if (!accessor) return 0;
  const count = accessor.count;
  return count as number;
}

function statsFromJson(value: unknown): ParsedModelStats | null {
  if (!isRecord(value)) return null;

  const accessors = optionalArray(value.accessors);
  const meshes = optionalArray(value.meshes);
  const materials = optionalArray(value.materials);
  const textures = optionalArray(value.textures);
  if (!accessors || !meshes || !materials || !textures) return null;
  if (!accessors.every(accessor => (
    isRecord(accessor)
    && typeof accessor.count === 'number'
    && Number.isSafeInteger(accessor.count)
    && accessor.count >= 0
  ))) return null;

  const validAccessors = accessors as Array<Record<string, unknown>>;

  let vertices = 0;
  let triangles = 0;

  for (const mesh of meshes) {
    if (!isRecord(mesh)) return null;
    const primitives = optionalArray(mesh.primitives);
    if (!primitives) return null;

    for (const primitive of primitives) {
      if (!isRecord(primitive)) return null;
      const attributes = primitive.attributes;
      if (attributes !== undefined && !isRecord(attributes)) return null;

      const positionCount = accessorCount(validAccessors, attributes?.POSITION);
      vertices += positionCount;

      const elementCount = primitive.indices === undefined
        ? positionCount
        : accessorCount(validAccessors, primitive.indices);
      const mode = primitive.mode ?? 4;
      if (typeof mode !== 'number' || !Number.isInteger(mode)) return null;

      if (mode === 4) triangles += Math.floor(elementCount / 3);
      else if (mode === 5 || mode === 6) triangles += Math.max(0, elementCount - 2);
      else if (mode < 0 || mode > 6) return null;
    }
  }

  return {
    vertices,
    triangles,
    meshes: meshes.length,
    materials: materials.length,
    textures: textures.length,
  };
}

function parseJsonBytes(bytes: Uint8Array): unknown {
  const text = new TextDecoder().decode(bytes).replace(/[\u0000\s]+$/u, '');
  return JSON.parse(text);
}

function combineChunks(chunks: Uint8Array[], length: number): Uint8Array {
  const combined = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return combined;
}

async function cancelReader(reader: ReadableStreamDefaultReader<Uint8Array>): Promise<void> {
  try {
    await reader.cancel();
  } catch {
    // The response may already have failed or been aborted. There is no useful
    // recovery work for this supplementary, fail-silent request.
  }
}

async function readModelStats(response: Response): Promise<ModelStats | null> {
  const reader = response.body?.getReader();
  if (!reader) return null;

  const chunks: Uint8Array[] = [];
  let length = 0;
  let format: 'glb' | 'gltf' | null = null;
  let glbJsonEnd: number | null = null;
  let glbFileSize: number | null = null;

  const append = (value: Uint8Array, start: number, end: number) => {
    if (end <= start) return;
    const chunk = value.slice(start, end);
    chunks.push(chunk);
    length += chunk.byteLength;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      const bytes = combineChunks(chunks, length);
      const stats = parseModelStats(bytes.buffer as ArrayBuffer);
      return stats ? { ...stats, fileSize: length } : null;
    }
    if (!value?.byteLength) continue;

    let offset = 0;
    if (format === null) {
      const magicBytesNeeded = 4 - length;
      const magicBytesTaken = Math.min(magicBytesNeeded, value.byteLength);
      append(value, 0, magicBytesTaken);
      offset = magicBytesTaken;

      if (length < 4) continue;
      const prefix = combineChunks(chunks, length);
      format = new DataView(prefix.buffer).getUint32(0, true) === GLB_MAGIC ? 'glb' : 'gltf';

      if (format === 'gltf') {
        const contentLength = Number(response.headers.get('content-length'));
        if (Number.isFinite(contentLength) && contentLength > MAX_MODEL_JSON_BYTES) {
          await cancelReader(reader);
          return null;
        }
      }
    }

    if (format === 'gltf') {
      if (length + value.byteLength - offset > MAX_MODEL_JSON_BYTES) {
        await cancelReader(reader);
        return null;
      }
      append(value, offset, value.byteLength);
      continue;
    }

    if (glbJsonEnd === null) {
      const headerBytesNeeded = GLB_JSON_START - length;
      const headerBytesTaken = Math.min(headerBytesNeeded, value.byteLength - offset);
      append(value, offset, offset + headerBytesTaken);
      offset += headerBytesTaken;
      if (length < GLB_JSON_START) continue;

      const header = combineChunks(chunks, length);
      const view = new DataView(header.buffer);
      if (view.getUint32(4, true) !== 2 || view.getUint32(16, true) !== JSON_CHUNK_TYPE) {
        await cancelReader(reader);
        return null;
      }

      glbFileSize = view.getUint32(8, true);
      const jsonLength = view.getUint32(12, true);
      glbJsonEnd = GLB_JSON_START + jsonLength;
      if (
        glbFileSize < GLB_JSON_START
        || glbJsonEnd < GLB_JSON_START
        || glbJsonEnd > glbFileSize
        || jsonLength > MAX_MODEL_JSON_BYTES
      ) {
        await cancelReader(reader);
        return null;
      }
    }

    const bytesNeeded = glbJsonEnd - length;
    append(value, offset, offset + Math.min(bytesNeeded, value.byteLength - offset));
    if (length === glbJsonEnd) {
      await cancelReader(reader);
      const bytes = combineChunks(chunks, length);
      try {
        const stats = statsFromJson(parseJsonBytes(bytes.subarray(GLB_JSON_START)));
        return stats && glbFileSize !== null ? { ...stats, fileSize: glbFileSize } : null;
      } catch {
        return null;
      }
    }
  }
}

export function parseModelStats(buffer: ArrayBuffer): ParsedModelStats | null {
  try {
    if (buffer.byteLength === 0) return null;

    if (buffer.byteLength >= 4 && new DataView(buffer, 0, 4).getUint32(0, true) === GLB_MAGIC) {
      if (buffer.byteLength < GLB_HEADER_LENGTH + CHUNK_HEADER_LENGTH) return null;

      const view = new DataView(buffer);
      if (view.getUint32(4, true) !== 2) return null;
      const declaredLength = view.getUint32(8, true);
      if (declaredLength < GLB_HEADER_LENGTH + CHUNK_HEADER_LENGTH || declaredLength !== buffer.byteLength) {
        return null;
      }

      const chunkLength = view.getUint32(GLB_HEADER_LENGTH, true);
      const chunkType = view.getUint32(GLB_HEADER_LENGTH + 4, true);
      const chunkStart = GLB_HEADER_LENGTH + CHUNK_HEADER_LENGTH;
      const chunkEnd = chunkStart + chunkLength;
      if (chunkType !== JSON_CHUNK_TYPE || chunkEnd > declaredLength || chunkEnd < chunkStart) return null;

      return statsFromJson(parseJsonBytes(new Uint8Array(buffer, chunkStart, chunkLength)));
    }

    return statsFromJson(parseJsonBytes(new Uint8Array(buffer)));
  } catch {
    return null;
  }
}

export async function fetchModelStats(url: string, signal?: AbortSignal): Promise<ModelStats | null> {
  if (!url || isUnviewableModelUrl(url)) return null;

  try {
    const response = signal ? await fetch(url, { signal }) : await fetch(url);
    if (!response.ok) return null;
    return await readModelStats(response);
  } catch {
    return null;
  }
}
